import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import "./IgnoreListModal.css";
import { ignoredAlprService } from "../utils/api";

interface IgnoreListModalProps {
  isOpen: boolean;
  lotId: string;
  lotIds?: string[]; // Optional array for multi-lot mode
  performedBy: string;
  onClose: () => void;
}

const sanitizeInput = (value: string) =>
  value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 50);

const IgnoreListModal: React.FC<IgnoreListModalProps> = ({
  isOpen,
  lotId,
  lotIds,
  performedBy,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [ignoredStrings, setIgnoredStrings] = useState<string[]>([]);
  const [originalStrings, setOriginalStrings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use lotIds if provided, otherwise use single lotId
  const effectiveLotIds = lotIds && lotIds.length > 0 ? lotIds : [lotId];
  const isMultiLotMode = effectiveLotIds.length > 1;

  useEffect(() => {
    if (!isOpen || effectiveLotIds.length === 0) return;

    let isMounted = true;
    const loadIgnoredStrings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let allStrings: string[] = [];

        if (isMultiLotMode) {
          // Fetch from all lots and merge unique values
          const results = await Promise.all(
            effectiveLotIds.map(id => ignoredAlprService.getIgnoredStringsByLot(id))
          );
          const uniqueStrings = new Set<string>();
          results.forEach(existing => {
            existing.forEach((value: string) => {
              const normalized = sanitizeInput(value);
              if (normalized) uniqueStrings.add(normalized);
            });
          });
          allStrings = Array.from(uniqueStrings);
        } else {
          const existing = await ignoredAlprService.getIgnoredStringsByLot(effectiveLotIds[0]);
          allStrings = existing.map((value: string) => sanitizeInput(value)).filter(Boolean);
        }

        if (!isMounted) return;

        const normalized = Array.from(new Set(allStrings)).sort();

        setIgnoredStrings(normalized);
        setOriginalStrings(normalized);
      } catch (err) {
        console.error("Failed to load ignored ALPR strings", err);
        if (isMounted) {
          setError("Failed to load ignore list. Please try again.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadIgnoredStrings();
    return () => {
      isMounted = false;
    };
  }, [isOpen, effectiveLotIds.join(",")]);

  const isDirty = useMemo(() => {
    const normalizedCurrent = [...ignoredStrings].sort().join("|");
    const normalizedOriginal = [...originalStrings].sort().join("|");
    return normalizedCurrent !== normalizedOriginal;
  }, [ignoredStrings, originalStrings]);

  const handleAdd = () => {
    const formatted = sanitizeInput(inputValue);
    if (!formatted) {
      setError("Enter letters or numbers to add a plate to ignore.");
      return;
    }

    if (ignoredStrings.includes(formatted)) {
      setError("That plate is already in the ignore list.");
      return;
    }

    setIgnoredStrings((prev) => [...prev, formatted].sort());
    setInputValue("");
    setError(null);
  };

  const handleRemove = (value: string) => {
    setIgnoredStrings((prev) => prev.filter((item) => item !== value));
  };

  const handleSave = async () => {
    if (effectiveLotIds.length === 0) return;

    setIsSaving(true);
    setError(null);
    try {
      // Save to all lots in multi-lot mode
      await Promise.all(
        effectiveLotIds.map(lid =>
          ignoredAlprService.bulkSaveIgnoredStrings(lid, ignoredStrings, performedBy)
        )
      );
      setOriginalStrings(ignoredStrings);
      onClose();
    } catch (err: any) {
      console.error("Failed to save ignore list", err);
      const message =
        err?.response?.data?.message || "Failed to save ignore list.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setInputValue("");
    setError(null);
    setIgnoredStrings(originalStrings);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      title="ALPR Ignore List"
      description='List of frequent misreads that are accidentally scanned to be ignored (e.g. the word "SAFETY" on a safety vehicle).'
      onCancel={handleCancel}
      onConfirm={handleSave}
      confirmText={isSaving ? "Saving..." : "Save"}
      disableConfirm={!isDirty || isSaving || isLoading}
    >
      <div className="ignore-list-modal">
        <div className="ignore-input-row">
          <input
            type="text"
            value={inputValue}
            maxLength={50}
            onChange={(e) => {
              setInputValue(sanitizeInput(e.target.value));
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="Enter string"
          />
          <button
            className="button-secondary ignore-add-button"
            onClick={handleAdd}
            disabled={!sanitizeInput(inputValue)}
          >
            Add
          </button>
        </div>

        {error && <div className="ignore-error">{error}</div>}

        {isLoading ? (
          <div className="ignore-loading">
            <div className="ignore-spinner" aria-hidden="true" />
            <span>Loading ignore list...</span>
          </div>
        ) : (
          <div className="ignore-tags-container">
            {ignoredStrings.length === 0 ? (
              <div className="ignore-empty">No ignored plates yet.</div>
            ) : (
              <div className="ignore-tags">
                {ignoredStrings.map((value) => (
                  <span key={value} className="ignore-tag">
                    <span className="ignore-tag-text">{value}</span>
                    <button
                      className="ignore-tag-remove"
                      onClick={() => handleRemove(value)}
                      aria-label={`Remove ${value}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default IgnoreListModal;
